// ABOUTME: Standalone day-progression chart (picked/shipped/returns over the day, per hour/30m/15m) for a BI
// ABOUTME: dashboard cell. Self-fetching: owns its timeframe state + day-progression query; `sx` merges into root.
import { Box, ButtonBase, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { tokens } from "../../../../../theme";
import Subheading from "../../../../../components/theme/text/Subheading";
import Text from "../../../../../components/theme/text/Text";
import { HorizontalBarChartContentReports } from "../../../../../components/theme/charts/HorizontalBarChart";
import { DropdownBase } from "../../../../../components/theme/dropdowns/Dropdown";
import { useGetDayProgressionQuery } from "../../../../../newapi/reports/fulfillment/reportFulfillmentSlice";

export const DaytotalsProgressionChart = ({ sx, preview }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const daytotalsDates = useSelector((state) => state.dayTotalsReport);

    const timeFrames = [
        { id: 0, value: "1h", column: t("per_hour"), barWidth: "28px", barRadius: 12 },
        { id: 1, value: "30m", column: t("30m"), barWidth: "14px", barRadius: 9 },
        { id: 2, value: "15m", column: t("15m"), barWidth: "14px", barRadius: 6 },
    ];
    const [activeTimeFrame, setActiveTimeFrame] = useState(timeFrames[0]);

    const { data: dayProgressionData, isLoading: progressionLoading, isFetching: progressionFetching, isError: progressionError, refetch: refetchProgression } = useGetDayProgressionQuery({
        userId: -1,
        date: daytotalsDates?.startDate,
        timeFrame: activeTimeFrame.value
    }, {
        pollingInterval: 300000,
        skip: preview,
    });

    return (
        <Box
            sx={{
                flex: 1,
                background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                px: 2,
                py: 1.5,
                borderRadius: 4,
                overflow: "hidden",
                minWidth: 0,
                ...sx,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    gap: 4,
                    alignItems: 'center',
                    "@media screen and (max-width: 47.9375em)": {
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 2,
                        py: 1.5,
                    }
                }}
            >
                <Box>
                    <Subheading bold>{t("processed_per")}</Subheading>
                </Box>
                <Box
                    sx={{
                        ml: "auto",
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                        boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                        height: 40,
                        px: 0.5,
                        borderRadius: 5,
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: 'center',
                        minWidth: 220,
                        "@media screen and (max-width: 47.9375em)": {
                            ml: "unset",
                            width: 1,
                        }
                    }}
                >
                    <Box
                        sx={{
                            height: 32,
                            lineHeight: "32px",
                            px: 1.5,
                            borderRadius: 4,
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            mr: 1.5,
                        }}
                    >
                        <Text>{t("timeframe")}</Text>
                    </Box>

                    <Text sx={{ pr: 2 }} bold>{t(activeTimeFrame.column)}</Text>

                    <DropdownBase
                        noPropagationOnSelect
                        stopPropagation
                        key={activeTimeFrame}
                        displayName={"column"}
                        onChange={(selected) => {
                            setActiveTimeFrame(timeFrames[selected])
                        }}
                        useTranslation
                        options={timeFrames}
                        selected={activeTimeFrame.id}
                        closeOnSelection
                        maxHeight={"unset"}
                        valueKey={"id"}
                        disallowDeselection
                    >
                        <ButtonBase
                            sx={{
                                ml: "auto",
                                width: 32,
                                height: 32,
                                background: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                                color: "#fff",
                                borderRadius: 5,
                            }}
                        >
                            <EditOutlinedIcon fontSize="small" />
                        </ButtonBase>
                    </DropdownBase>
                </Box>
            </Box>
            {(dayProgressionData || preview) &&
                <HorizontalBarChartContentReports
                    hideFilter
                    width={activeTimeFrame.id === timeFrames[0].id ? ((dayProgressionData?.picked?.categories?.length ?? 0) * 112) : ((dayProgressionData?.picked?.categories?.length ?? 0) * 72)}
                    title={t("inbound")}
                    subtitle={t("last_7_days_and_today")}
                    type="bar"
                    staticColors={[
                        colors.primary[400],
                        colors.green[400],
                        colors.orange[400],
                    ]}
                    isError={progressionError}
                    isLoading={preview || progressionLoading || progressionFetching}
                    categories={dayProgressionData?.picked?.categories ?? []}
                    values={[
                        ...(dayProgressionData?.picked?.series || []),
                        ...(dayProgressionData?.shipped?.series || []),
                        ...(dayProgressionData?.returns?.series || [])
                    ]}
                    color={"default"}
                    onRetry={refetchProgression}
                    plotOptions={{
                        bar: {
                            borderRadius: activeTimeFrame.barRadius ?? 12,
                            distributed: false,
                            columnWidth: activeTimeFrame.barWidth ?? "28px",
                            dataLabels: {
                                position: "top",
                            },
                        }
                    }}
                    sx={{
                        box: {
                            mt: 1,
                            mb: "-32px",
                            overflowX: "scroll"
                        }
                    }}
                />
            }
        </Box>
    );
};
